import { ObjectTools } from '../../tools/object-tools';
import { SolColor } from './sol-color';

/**
 * A badge on whatever it wraps: a dot for "something is here", or a count where the number matters.
 * `corner` places it on the target's box, `inset` onto content that sits inside padding.
 */
export type SolBadgePlacement = 'corner' | 'inset';

export class SolBadge {
  public static readonly DEFAULT_MAX_COUNT = 99;

  private constructor(
    public readonly placement: SolBadgePlacement,
    public readonly visible: boolean,
    public readonly color: SolColor,
    public readonly count: number | undefined,
    public readonly maxCount: number,
    public readonly countVisible: boolean,
    public readonly dotVisible: boolean,
    public readonly displayCount: string,
    public readonly backgroundColor: string,
  ) {}

  public static of(): SolBadge {
    return new SolBadge(
      'corner',
      false,
      'primary',
      undefined,
      SolBadge.DEFAULT_MAX_COUNT,
      false,
      true,
      '',
      '',
    ).recalculate();
  }

  public withPlacement(placement: SolBadgePlacement): SolBadge {
    if (this.placement === placement) {
      return this;
    }
    return this.copy({ placement });
  }

  public withVisible(visible: boolean): SolBadge {
    if (this.visible === visible) {
      return this;
    }
    return this.copy({ visible });
  }

  public withColor(color: SolColor): SolBadge {
    if (this.color === color) {
      return this;
    }
    return this.copy({ color }).recalculate();
  }

  public withCount(count: number | undefined): SolBadge {
    if (this.count === count) {
      return this;
    }
    return this.copy({ count }).recalculate();
  }

  public withMaxCount(maxCount: number): SolBadge {
    const next = Number.isFinite(maxCount) && maxCount > 0 ? Math.floor(maxCount) : SolBadge.DEFAULT_MAX_COUNT;
    if (this.maxCount === next) {
      return this;
    }
    return this.copy({ maxCount: next }).recalculate();
  }

  private recalculate(): SolBadge {
    const countVisible = this.count !== undefined;
    return this.copy({
      countVisible,
      dotVisible: !countVisible,
      displayCount: SolBadge.toDisplayCount(this.count, this.maxCount),
      backgroundColor: `var(--sol-color-${this.color})`,
    });
  }

  private static toDisplayCount(count: number | undefined, maxCount: number): string {
    if (count === undefined) {
      return '';
    }
    return count > maxCount ? `${maxCount}+` : `${count}`;
  }

  private copy(props: Partial<SolBadge>): SolBadge {
    return ObjectTools.copy<SolBadge>(this, props);
  }
}
